import type { FilterItemMongoType, GeojsonType, QueryGeometryType, QueryType } from '../../types';
import type { ClusterCfgType, FilterSubMenuValue } from '../../types-analytics';
import type { API_DataType } from '../../types-telematics';
import { smartRounding } from '../../tools/smartRounding';
import FilterConfig from './FilterConfig';
import { _ } from '../../Lang.class.js';
import { ResolutionToday } from '../Colorizer/ResolutionToday';
import { ResolutionDate } from '../Colorizer/ResolutionDate';

export type TranslateFilterStrType = {
    negation: boolean;
    // title: string;
    // value: string;
    filter_str: string;
};

class FilterTools {
    extractFromMongo(key: string, filterItem: QueryType | {}): FilterItemMongoType {
        let output: FilterItemMongoType = { variable: key, condition: '', values: null };
        try {
            output.condition = Object.keys(filterItem)[0].toString() ?? '';
        } catch (e) {
            // console.warn('INVALID extractFromMongo, filterItem, output', filterItem, output, e.message); // FIXME: silented warning without fix
        }
        try {
            output.values = (Object.values(filterItem)[0] ?? null) as
                | string
                | number
                | string[]
                | number[];
        } catch (e) {
            // console.warn('INVALID extractFromMongo, filterItem, output', filterItem, output, e.message); // FIXME: silented warning without fix
        }
        return output;
    }

    translateFilterItem(
        key: string,
        filterItem: QueryType,
        unit: string,
        filter_str: string,
        parameters: (string | number)[],
        dataType: API_DataType
    ): TranslateFilterStrType {
        console.debug(
            'translateFilterItem',
            key,
            filterItem,
            unit,
            filter_str,
            parameters,
            dataType
        );
        if (key === 'geom' && filter_str === '') {
            // FIXME: move it to config
            console.log('@weblate, translateFilterItem, geom, parameters', key, parameters);
            if (parameters.length === 0) {
                return {
                    negation: false,
                    filter_str: _('we wskazanym obszarze'),
                };
            } else {
                filter_str = _('w promieniu %s od %s');
                unit = unit === '' ? 'm' : unit;
            }
        }
        if (filter_str) {
            console.log(
                '@weblate, (by filter_str) key, filter_str, filterItem',
                key,
                filter_str,
                filterItem
            );
            let { weblate_key, weblate_string, weblate_human } =
                this._translateFilterStrWithWeblate(
                    key,
                    filterItem,
                    filter_str,
                    parameters,
                    unit,
                    dataType
                );
            if (weblate_key === weblate_string) {
                // If not found key in weblate...
                console.log('@weblate, weblate_key === weblate_string', weblate_key);
                return this._translateFilterStr(
                    key,
                    filterItem,
                    filter_str,
                    parameters,
                    unit,
                    dataType
                );
            }
            return {
                negation: weblate_human.includes('class="negation"'),
                filter_str: weblate_human,
            };
        } else {
            console.log(
                '@weblate, (by weblate) key, filter_str, filterItem',
                key,
                filter_str,
                filterItem
            );
            return {
                filter_str: this._translateFilterItemWithWeblate(key, filterItem, unit, parameters),
                negation: false,
            };
        }
    }

    getValuesAsStringRounded(
        condition: string,
        filterItem: QueryType,
        unit: string,
        dataType: API_DataType
    ): string {
        if (dataType === 'date' || dataType === 'time' || dataType === 'datetime') {
            switch (condition) {
                case '$between': {
                    let a = filterItem.$between[0] as string;
                    let b = filterItem.$between[1] as string;
                    if (
                        ResolutionToday.isValidTodayString(a) &&
                        ResolutionToday.isValidTodayString(b)
                    ) {
                        let va = ResolutionToday.getDaysFromTodayString(a) as number;
                        let vb = ResolutionToday.getDaysFromTodayString(b) as number;
                        let fromStr =
                            va < 0
                                ? -va + ' ' + _('filter_days_ago')
                                : va > 0
                                  ? _('filter_for') + ' ' + va + ' ' + _('filter_days')
                                  : _('filter_today');
                        let toStr =
                            vb < 0
                                ? -vb + ' ' + _('filter_days_ago')
                                : vb > 0
                                  ? _('filter_for') + ' ' + vb + ' ' + _('filter_days')
                                  : _('filter_today');
                        if (fromStr === toStr) {
                            return `${fromStr}`;
                        }
                        return `${_('filter_between')} ${fromStr} ${_('filter_and')} ${toStr}`;
                    }
                    if (dataType === 'date') {
                        a = ResolutionDate.getDateStringFromDays(
                            ResolutionDate.getDaysFromDate(new Date(a)),
                            true
                        );
                        b = ResolutionDate.getDateStringFromDays(
                            ResolutionDate.getDaysFromDate(new Date(b)),
                            true
                        );
                    }
                    return `${a} - ${b}`;
                }
                default: {
                    let a = filterItem[condition] as string;
                    if (ResolutionToday.isValidTodayString(a)) {
                        let va = ResolutionToday.getDaysFromTodayString(a) as number;
                        if (va < 0) {
                            return `${-va} ` + _('filter_days_ago');
                        }
                        if (va === 0) {
                            return _('filter_today');
                        }
                        return _('filter_for') + ` ${va} ` + _('filter_days');
                    }
                    if (dataType === 'date') {
                        a = ResolutionDate.getDateStringFromDays(
                            ResolutionDate.getDaysFromDate(new Date(a)),
                            true
                        );
                    }
                    return `${a}`;
                }
            }
        } else {
            switch (condition) {
                case '$geoWithin':
                    return '';
                case '$between': {
                    let v0 = filterItem.$between[0];
                    let v1 = filterItem.$between[1];
                    let [roundedValue0, roundedValue1] =
                        new smartRounding().getRoundedValuesWithTheSameUnit([v0, v1], unit, {
                            convert_nicer: true,
                        });
                    return [
                        roundedValue0.rounded_value,
                        // _('i'),
                        '-',
                        roundedValue1.rounded_value,
                        roundedValue1.rounded_title,
                        this._formatedUnitInHtml(roundedValue1.rounded_unit),
                    ]
                        .join(' ')
                        .trim()
                        .replace(/\s\s/g, ' ');
                }
                case '$arr_in':
                case '$arr_nin': {
                    // return as comma separated values, but with rules:
                    // 1) if 1-3 values return list comma separated
                    // 2) if more than 3 values return first 2 values and +N more
                    let valuesArray: (string | number)[] = [];
                    if (condition === '$arr_in')
                        valuesArray = filterItem.$arr_in as (string | number)[];
                    if (condition === '$arr_nin')
                        valuesArray = filterItem.$arr_nin as (string | number)[];
                    const maxListedValues = 3;
                    if (valuesArray.length <= maxListedValues) {
                        return valuesArray.join(', ');
                    } else {
                        let firstThree = valuesArray.slice(0, maxListedValues - 1).join(', ');
                        let remainingCount = valuesArray.length - maxListedValues + 1;
                        if (remainingCount <= 4) {
                            return `${firstThree} i ${remainingCount} ${_('inne')}`;
                        } else {
                            return `${firstThree} i ${remainingCount} ${_('innych')}`;
                        }
                    }
                }

                default: {
                    let roundedValue = new smartRounding().getRoundedValues(
                        parseFloat(filterItem[condition] as string),
                        unit,
                        { convert_nicer: true }
                    );
                    return [
                        roundedValue.rounded_value,
                        roundedValue.rounded_title,
                        this._formatedUnitInHtml(roundedValue.rounded_unit),
                    ]
                        .join(' ')
                        .trim();
                }
            }
        }
    }

    _formatedUnitInHtml(unit: string): string {
        if (unit === 'm2') {
            return `m<sup>2</sup>`;
        }
        if (unit === 'km2') {
            return `km<sup>2</sup>`;
        }
        return `${unit}`;
    }

    _translateFilterStrWithWeblate(
        key: string,
        filterItem: QueryType,
        filter_str: string,
        parameters: (string | number)[],
        unit: string,
        dataType: API_DataType
    ): { weblate_key: string; weblate_string: string; weblate_human: string } {
        let { variable, condition, values } = this.extractFromMongo(key, filterItem);

        console.log(
            '_translateFilterStrWithWeblate, filterItem',
            filterItem,
            'filter_str',
            filter_str,
            'parameters',
            parameters,
            'unit',
            unit,
            'dataType',
            dataType,
            'variable',
            variable,
            'condition',
            condition,
            'values',
            values
        );

        let negation: boolean = ['$nin', '$arr_nin', '$neq'].includes(condition);

        if (filter_str.endsWith(' m')) {
            // remove this ' m'
            filter_str = filter_str.slice(0, -2);
            unit = 'm';
        }

        if (parameters && parameters.length) {
            console.log('_translateFilterStr, parameters', parameters);
            for (let i = 0; i < parameters.length; i++) {
                filter_str = filter_str.replace(
                    /%s/,
                    '<span class="filter-description-item__value" data-id="parameters">' +
                        parameters[i].toString() +
                        '</span>'
                );
            }
        }

        let weblate_key =
            'filter_human.' +
            this.buildTranslateKey(
                variable,
                condition,
                Array.isArray(values) ? values.length : null
            );

        filter_str = _(weblate_key);

        if (typeof filterItem[condition] === 'string') {
            filter_str = filter_str.replace(
                /%s/,
                '<span class="filter-description-item__value" data-id="filterItemConditionIsString">' +
                    filterItem[condition].toString() +
                    '</span>'
            );
        } else {
            // not used?
            filter_str = filter_str.replace(
                /%s/,
                '<span class="filter-description-item__value" data-id="filterItemConditionNotString">' +
                    this.conditionTranslated(condition, filter_str) +
                    ' ' +
                    this.getValuesAsStringRounded(condition, filterItem, unit, dataType) +
                    '</span>'
            );
        }

        if (negation) {
            filter_str = `<span class="negation">${filter_str}</span>`;
        }

        console.log(
            '@weblate, _translateFilterStrWithWeblate, filter_str',
            filter_str,
            weblate_key
        );

        return {
            weblate_key: weblate_key,
            weblate_string: _(weblate_key),
            weblate_human: filter_str.trim(),
        };
    }

    conditionTranslated(condition: string, filter_str: string, dataType: API_DataType): string {
        switch (condition) {
            case '$between': {
                // if (dataType === 'date' || dataType === 'time' || dataType === 'datetime') {
                //     return '';
                // }
                // return _('pomiędzy');
                return '';
            }
            case '$gt': {
                // if (dataType === 'date' || dataType === 'time' || dataType === 'datetime') {
                //     return _('później niż');
                // }
                // return _('większ$1 od');
                return '&#62;';
            }
            case '$lt': {
                return '&#60;';
                // if (dataType === 'date' || dataType === 'time' || dataType === 'datetime') {
                //     return _('wcześniej niż');
                // }
                // return _('mniejsz$1 od');
            }
            case '$gte': {
                // if (dataType === 'date' || dataType === 'time' || dataType === 'datetime') {
                //     // return _('później lub w');
                //     return '>=';
                // }
                // return _('większ$1 lub równ$1');
                return '&#8805;';
            }
            case '$lte': {
                // if (dataType === 'date' || dataType === 'time' || dataType === 'datetime') {
                //     return _('<=');
                // }
                return '&#8804;';
                // return _('mniejsz$1 lub równ$1');
            }
            case '$eq': {
                // return _('równ$1');
                return '';
            }
            case '$neq': {
                // return _('różn$1');
                return _('filter_not');
            }
            default:
                return condition;
        }
    }

    // will translate only filter_str without values
    _translateFilterStr(
        key: string,
        filterItem: QueryType,
        filter_str: string,
        parameters: (string | number)[],
        unit: string,
        dataType: API_DataType
    ): TranslateFilterStrType {
        // console.log(
        //     '#444 _translateFilterStr, input: ',
        //     key,
        //     filterItem,
        //     filter_str,
        //     parameters,
        //     unit,
        //     dataType
        // );
        let output: TranslateFilterStrType = { negation: false, filter_str: '---' };

        let tagValueStart = `<span class="filter-description-item__value" data-id="_translateFilterStr" data-key="${key}" data-unit="${unit}">`;
        // data-filter-str="${filter_str}" removed due to invalid chars
        let tagValueEnd = '</span>';

        let { variable, condition, values } = this.extractFromMongo(key, filterItem);
        output.negation = ['$nin', '$arr_nin', '$neq'].includes(condition);

        if (parameters && parameters.length) {
            // console.log('_translateFilterStr, parameters', parameters);
            for (let i = 0; i < parameters.length; i++) {
                let v = parameters[i];
                if (typeof v === 'number') {
                    // console.debug('_translateFilterStr, v', v, typeof v, unit);
                    let rounded = new smartRounding().getRoundedValues(
                        parseFloat(v as unknown as string),
                        unit,
                        { convert_nicer: true }
                    );
                    // console.debug('_translateFilterStr, rounded', rounded);
                    output.filter_str = filter_str.replace(
                        /%s/,
                        `${rounded.rounded_value} ${rounded.rounded_title} ${this._formatedUnitInHtml(rounded.rounded_unit)}`
                    );
                    // console.debug('_translateFilterStr, filter_str', filter_str);
                } else {
                    output.filter_str = filter_str.replace(/%s/, v.toString());
                }
            }
            return output;
        }

        if (filter_str.endsWith(' m') && (unit === 'm' || unit === '')) {
            // unit will be added later
            filter_str = filter_str.slice(0, -2);
            unit = 'm';
        }

        // Convert `z l. ludności` to `z liczbą ludności` (to be supported by getDetectSex)
        filter_str = filter_str.replace(/z\sl\.\s/, _('filter_with_number') + ' ');
        //
        // console.log('#444 _translateFilterStr, after parameters', filter_str);
        // console.log('#444 _translateFilterStr, negation', output.negation);

        filter_str = filter_str.replace(
            /%v(.+?)%/,
            tagValueStart + this.conditionTranslated(condition, filter_str, dataType) + tagValueEnd
        );

        // console.log('#444 _translateFilterStr, after %v', filter_str);

        filter_str = filter_str.replace(/%</, '');
        filter_str = filter_str.replace(/%>/, '');
        filter_str = filter_str.replace(/%d/, '');

        // if (['$nin', '$in', '$arr_in', '$arr_nin'].includes(condition)) {
        if (['$nin', '$in'].includes(condition)) {
            // console.log('_translateFilterStr, $nin, $in', filterItem[condition], filter_str);
            filter_str = filter_str.replace(/%s/g, '');
            // console.log('#444 _translateFilterStr, after $nin,$in', filter_str);
        } else {
            if (filter_str.match(/%s/)) {
                if (['$eq', '$neq'].includes(condition)) {
                    filter_str = filter_str.replace(
                        /%s/,
                        tagValueStart + _(filterItem[condition].toString()) + tagValueEnd
                    );
                    // console.log('#444 _translateFilterStr, after $eq,$neq', filter_str);
                } else {
                    filter_str = filter_str.replace(
                        /%s/,
                        tagValueStart +
                            this.getValuesAsStringRounded(condition, filterItem, unit, dataType) +
                            tagValueEnd
                    );
                    // console.log('#444 _translateFilterStr, after others aaa ', filter_str);
                }
            } else if (filter_str.match(/%cmp%/)) {
                if (['$eq', '$neq'].includes(condition)) {
                    filter_str = filter_str.replace(
                        /%cmp%/,
                        ' ' + tagValueStart + filterItem[condition].toString() + tagValueEnd
                    );
                    // console.log('#444 _translateFilterStr, after $eq,$neq', filter_str);
                } else {
                    filter_str = filter_str.replace(
                        /%cmp%/,
                        ' ' +
                            tagValueStart +
                            this.getValuesAsStringRounded(condition, filterItem, unit, dataType) +
                            tagValueEnd
                    );
                    // console.log('#444 _translateFilterStr, after others bbb ', filter_str);
                }
            } else {
                if (filterItem.type === 'MultiPolygon') {
                    filter_str = filter_str.trim();
                } else {
                    filter_str =
                        filter_str.trim() +
                        ': ' +
                        tagValueStart +
                        this.getValuesAsStringRounded(
                            condition,
                            filterItem,
                            unit,
                            dataType
                        ).trim() +
                        tagValueEnd;
                    // console.log('#444 _translateFilterStr, after others ccc ', filter_str);
                }
            }
        }

        // console.log('#444 _translateFilterStr, before cleanup spaces', filter_str);

        filter_str = filter_str.replace(/%cmp%/, '');
        filter_str = filter_str.replace(/\s\s/g, ' '); // remove double spaces
        // console.log('#444 _translateFilterStr, after cleanup spaces', filter_str);

        filter_str = filter_str.replace(/\$in/g, ''); // remove $in from 'najszybszy dojazd do magazynu $in Janki (w kolekcji Kurierzy AM Paczka)
        // console.log('#444 _translateFilterStr, after cleanup', filter_str);

        // if (negation && false) {
        //     filter_str = filter_str.trim();
        //     filter_str = `<span class="negation" data-parent="_translateFilterStr">${_('filter_not')} ${_(filter_str)}&nbsp;</span>`;
        //     console.log('#444 _translateFilterStr, negation wrap', filter_str);
        // }
        //
        // console.log(
        //     '_translateFilterStr, output',
        //     tagValueStart,
        //     ' ||| ',
        //     tagValueEnd,
        //     ' ||| ',
        //     filter_str
        // );

        output.filter_str = filter_str.trim();
        return output;
    }

    _translateFilterItemWithWeblate(
        key: string,
        filterItem: QueryType,
        unit: string,
        parameters: (string | number)[]
    ): string {
        // console.log('translateFilterItem', filterItem, unit);
        let { variable, condition, values } = this.extractFromMongo(key, filterItem);
        let locale_variable = _(variable) as unknown as string; // Convert String to string
        let locale_condition = _('filter_conditions.' + condition) as unknown as string; // Convert String to string
        // let output_string: string = `jeśli wartość <span class="text-bold">${locale_variable}</span> jest ${condition} dla ${JSON.stringify(values)}`;
        let output_string: string = ``;
        try {
            // output_string = this.translateFilterItemByVariable(variable, condition, values, unit);
            let translate_key = 'filter_human.' + this.buildTranslateKey(variable, condition);
            output_string = _(translate_key);

            // If translation is not found, use default
            if (output_string === translate_key) {
                output_string = _('filter_human.default');
            }

            output_string = output_string.replace('{{variable}}', locale_variable);
            output_string = output_string.replace('{{condition}}', locale_condition);
            output_string = output_string.replace('{{values}}', JSON.stringify(values));
            if (typeof values === 'string') {
                output_string = output_string.replace('{{value}}', encodeURIComponent(values));
            } else {
                output_string = output_string.replace(
                    '{{value}}',
                    encodeURIComponent(values[0] as unknown as string)
                );
            }
            output_string = output_string.replace('{{value1}}', JSON.stringify(values[0]));
            output_string = output_string.replace('{{value2}}', JSON.stringify(values[1]));
            output_string = output_string.replace('{{unit}}', unit ? `[${unit}]` : '');
        } catch (e) {
            console.warn('Filter item translation error', e.message);
        }
        return output_string;
    }

    buildTranslateKey(variable: string, condition: string, quantity: number | null = null): string {
        if (quantity === null) {
            return `${variable}.${condition}`;
        }
        if (quantity === 1) {
            return `${variable}.${condition}.one`;
        }
        if (quantity === 2) {
            return `${variable}.${condition}.two`;
        }
        return `${variable}.${condition}.many`;
    }

    getGeojsonFromCoords(coords: number[][][]): GeojsonType {
        return { type: 'Polygon', coordinates: coords };
    }

    getFilterItemFromGeojson(geojson: GeojsonType): QueryGeometryType {
        // return { '$geoWithin': { '$geometry': geojson } };
        return { ...geojson };
    }

    convertToFilterItem(mongoCondition: FilterItemMongoType, dataType: API_DataType): QueryType {
        return {
            [mongoCondition.condition]: this._getParsedValueIfNeeded(
                mongoCondition.values,
                dataType
            ),
        };
    }

    _getParsedValueIfNeeded(
        value: any,
        dataType: API_DataType
    ): number | string | (number | string)[] {
        // console.log('_getParsedValueIfNeeded', value, dataType);
        if (Array.isArray(value) && !['geometry'].includes(dataType)) {
            return value.map(v => this._getParsedValueIfNeeded(v, dataType) as any);
        } else {
            switch (dataType) {
                case 'int':
                case 'int_array':
                    return Math.round(parseFloat(value));
                case 'double':
                    return parseFloat(value);
                case 'string':
                case 'string_array':
                    return String(value);
                default:
                    return value;
            }
        }
    }

    getMongoConditionFromValues(
        displayMethod: string,
        values: string | number | string[] | number[] | number[][][],
        isNegation: boolean,
        dataType: API_DataType
    ): QueryType {
        console.debug('getMongoConditionFromValues', displayMethod, values, isNegation, dataType);
        let mongoCondition: QueryType = {};
        switch (displayMethod) {
            case FilterConfig.displayMethod.BETWEEN: {
                if (values[0] !== null) {
                    mongoCondition['$gte'] = this._getParsedValueIfNeeded(values[0], dataType);
                }
                if (values[1] !== null) {
                    mongoCondition['$lte'] = this._getParsedValueIfNeeded(values[1], dataType);
                }
                if (values[0] !== null && values[1] !== null) {
                    mongoCondition = { $between: this._getParsedValueIfNeeded(values, dataType) };
                }
                return mongoCondition;
            }
            case FilterConfig.displayMethod.SELECT_MULTIPLE: {
                if (['int_array', 'string_array'].includes(dataType)) {
                    if (isNegation) {
                        mongoCondition = {
                            $arr_nin: this._getParsedValueIfNeeded(values, dataType),
                        };
                    } else {
                        mongoCondition = {
                            $arr_in: this._getParsedValueIfNeeded(values, dataType),
                        };
                    }
                } else {
                    if (isNegation) {
                        mongoCondition = { $nin: this._getParsedValueIfNeeded(values, dataType) };
                    } else {
                        mongoCondition = { $in: this._getParsedValueIfNeeded(values, dataType) };
                    }
                }
                return mongoCondition;
            }
            case FilterConfig.displayMethod.SINGLE:
            case FilterConfig.displayMethod.SELECT_SINGLE: {
                if (isNegation) {
                    mongoCondition = {
                        $neq: this._getParsedValueIfNeeded(values, dataType) ?? null,
                    };
                } else {
                    mongoCondition = {
                        $eq: this._getParsedValueIfNeeded(values, dataType) ?? null,
                    };
                }
                return mongoCondition;
            }
            case FilterConfig.displayMethod.AREA: {
                // @ts-ignore // values should be number[][][] here
                return this.getGeojsonFromCoords(values); // FIXME: check if have $geoWithin
                // return { $geoWithin: this.getGeojsonFromCoords(values) }; // FIXME: check if have $geoWithin
            }
            default: {
                console.error('getMongoConditionFromValues, unknown displayMethod', displayMethod);
                return null;
            }
        }
    }

    getValuesFromBetweenMongoCondition(
        mongoCondition: QueryType
    ): string[] | number[] | string | number | null {
        // let mongoCondition = { '$between': [33, 50] };
        if (mongoCondition === null) return null;
        if (Object.keys(mongoCondition).length === 0) return null;
        let values = null;
        let key = Object.keys(mongoCondition)[0];
        if (mongoCondition[key]) {
            values = mongoCondition[key];
        }
        return values;
    }

    isValueHasValidFormat(value: string, dataType: API_DataType): boolean {
        let valid = false;
        // console.log('isValueHasValidFormat, value', value, 'dataType', dataType, 'this.dataTypesValidationRules[dataType]', FilterConfig.dataTypesValidationRules[dataType])
        if (FilterConfig.dataTypesValidationRules?.[dataType] === undefined) {
            console.warn(
                'isValueHasValidFormat, dataType',
                dataType,
                'is not defined in this.dataTypesValidationRules',
                FilterConfig.dataTypesValidationRules
            );
            return false;
        }
        for (const rule of FilterConfig.dataTypesValidationRules[dataType]) {
            const regex = new RegExp(rule, 'ig');
            if (regex.test(value)) {
                valid = true;
                break;
            } else {
                console.debug(
                    'isValueHasValidFormat, Value',
                    value,
                    'does not match type ',
                    dataType,
                    ' with rule ',
                    rule,
                    'regex',
                    regex
                );
            }
        }
        return valid;
    }

    parseString(value: string | number | null): string {
        if (value === null) {
            return '';
        }
        return value.toString().trim();
    }

    matchesObjects(obj1: object, obj2: object): boolean {
        let match = true;
        if (Object.keys(obj1).length !== Object.keys(obj2).length) {
            return false;
        }
        Object.keys(obj1).forEach((key: string) => {
            if (obj1[key] !== obj2[key]) {
                match = false;
            }
        });
        return match;
    }

    getFieldNameWithGivenClusterCfg(
        filterSubMenu: { [key: string]: FilterSubMenuValue },
        cluster_cfg: ClusterCfgType
    ): string {
        let fieldName: string = '';

        Object.keys(filterSubMenu).forEach((key: string) => {
            filterSubMenu[key].items.forEach(item => {
                if (this.matchesObjects(item.params, cluster_cfg)) {
                    fieldName = item.id;
                }
            });
        });

        return fieldName;
    }
}

export default FilterTools;
